import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useWorklist } from '../hooks/useWorklist';
import { useAuthStore } from '../store/auth';
import { WorkRow, WorkSummary } from './WorkItemList';

/** Rows in the greeting. The rest are one press away; a wall of tasks at login helps nobody. */
const MAX_ROWS = 5;

/**
 * A TASK, not a "thing".
 *
 * Every row here is a job to do, and a single job routinely holds dozens of records — one row on
 * this list reads "say yes or no to 39 orders". Calling the rows "things" put the sentence "19
 * things need you today" directly above a row displaying the number 39, which is unreadable: the
 * reader cannot tell what is being counted, and reasonably concludes one of the two figures is
 * broken. "Task" is the word that can contain a number bigger than itself.
 */
const task = (n: number) => (n === 1 ? '1 task' : `${n} tasks`);

const SEEN_KEY = 'gb-mywork-greeted';

/** Today in the user's own timezone — the greeting is a per-day thing, not a per-24-hours thing. */
const today = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, locale-stable

/**
 * Whether this user has already been greeted today. Per-user, because a shared office machine is
 * the normal case here: the manager signing in after the storekeeper must get their own greeting,
 * not inherit the one that was already dismissed.
 *
 * Every access is wrapped — a browser with site data blocked throws on read, and the greeting is
 * not worth a white screen. Failing to read means we greet; failing to write means we may greet
 * twice. Both are the harmless direction of that trade.
 */
function greetedToday(userId: string): boolean {
  try {
    return localStorage.getItem(`${SEEN_KEY}:${userId}`) === today();
  } catch {
    return false;
  }
}

function markGreeted(userId: string) {
  try {
    localStorage.setItem(`${SEEN_KEY}:${userId}`, today());
  } catch { /* private window / storage blocked — greeting simply repeats */ }
}

function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The first-login-of-the-day greeting: "here is what needs you today".
 *
 * WHEN IT DOES NOT APPEAR, which is most of the design:
 *  · Not on the day's second sign-in. Once a day, per user — a nag on every login teaches people to
 *    dismiss it without reading, which is worse than not showing it.
 *  · Not when there is nothing to do. An empty dialog is a small insult; the all-clear belongs on
 *    the page, where the user went looking for it, not in the doorway.
 *  · Not while the list is still loading. It opens once, with content, rather than flashing an
 *    empty box and filling in — which reads as a glitch to someone unsure of the software.
 *
 * It only ever SUMMARISES. Every action lives on the page or the real screen behind the row, so
 * dismissing this can never lose the user anything.
 */
export function MyWorkGreeting() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? '';
  const { data, isLoading } = useWorklist(!!userId);
  const [open, setOpen] = useState(false);

  const act = (data?.items ?? []).filter((i) => i.group === 'ACT');

  useEffect(() => {
    if (!userId || isLoading || !data) return;
    if (!act.length) return;            // nothing to say
    if (greetedToday(userId)) return;   // already said it today
    markGreeted(userId);                // written on OPEN, so a refresh mid-read doesn't re-greet
    setOpen(true);
    // Keyed on the load completing, not on `act` — re-running as counts drift would re-open a
    // dialog the user had already closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isLoading, !!data]);

  const shown = act.slice(0, MAX_ROWS);
  const more = act.length - shown.length;

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      width={620}
      title={
        <span className="flex flex-col gap-2">
          <span className="flex flex-col gap-0.5">
            <span className="type-section-title text-content">
              {timeGreeting()}{data?.name ? `, ${data.name}` : ''}
            </span>
            <span className="type-body text-muted">
              {task(act.length)} {act.length === 1 ? 'needs' : 'need'} you today.
            </span>
          </span>
          {/* What the list is MADE of, before it is read: the money riding on it and the subject
              mix. It describes ALL the work, not the five rows that fit below — which is exactly
              why every subject is chipped and none is dropped: the chips add up to the count in the
              line above, and that sum is the only thing telling the reader they are looking at a
              summary of nineteen rather than a miscount of five.

              Each chip opens the list it counts. `null` is the money total, whose slice is every row
              carrying an amount — NOT the "Payments" subject, which is a different set.

              `mine=1` is what makes the destination keep the chip's promise: this greeting counts
              only work assigned to the reader, and /my-work lists work sitting with other people
              too, so without it a chip reading "Others 1" opened a tab headed "Others (3)". */}
          <WorkSummary
            items={act}
            hrefFor={(cat) => `/my-work?mine=1&lane=${cat ? `cat:${cat}` : 'money'}`}
            onNavigate={() => setOpen(false)}
          />
        </span>
      }
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {/* "Close" first in the DOM but shown second, so the primary action is the one a thumb
              reaches on a phone and the one keyboard focus lands on. */}
          <Button size="lg" onClick={() => setOpen(false)}>Close</Button>
          <Button size="lg" variant="primary" onClick={() => { setOpen(false); navigate('/my-work'); }}>
            See all my work
          </Button>
        </div>
      }
    >
      {/* Names what the list below IS, at the point where it would otherwise be mistaken for the
          whole. The breakdown strip above counts every task; these are its head. Without this line
          a chip reading "Payments 6" sits immediately above five visible cards and looks like a
          number that failed to match its own list. */}
      <p className="mb-2 type-caption text-muted">
        {more > 0 ? `The ${shown.length} most pressing of them:` : 'Most pressing first:'}
      </p>
      <div className="overflow-hidden rounded-card [&>button:first-child]:rounded-t-card [&>button:last-child]:rounded-b-card [&>button+button]:border-t-0">
        {shown.map((i) => <WorkRow key={i.key} item={i} onNavigate={() => setOpen(false)} />)}
      </div>
      {more > 0 && (
        <p className="mt-3 text-center type-caption text-muted">
          {more === 1 ? '1 more task is' : `${more} more tasks are`} waiting — press “See all my work”.
        </p>
      )}
    </Modal>
  );
}
